import React from 'react';

import { ProgramTypeNameEnum, ProgramTypesList } from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Table, { TableCell, TableRow } from '@amzn/meridian/table';
import Text from '@amzn/meridian/text';

import { Toggle } from 'src/components/Toggle';
import { getCamelStrFromAllCaps } from 'src/util/RateCards';

import './ViewProgramType.scss';

interface IViewProgramTypeProps {
    programTypeList?: ProgramTypesList;
}

export const ViewProgramType = ({ programTypeList }: IViewProgramTypeProps) => {
    // Check if we only have DEFAULT program type
    const isDefault =
        !!programTypeList &&
        programTypeList.length === 1 &&
        programTypeList[0].programTypeName === ProgramTypeNameEnum.DEFAULT;

    // Check if we have any program types
    const hasProgramTypes = !!programTypeList && programTypeList.length > 0 && !isDefault;

    // Check if a specific program type exists in the list
    const hasProgramType = (programTypeName: string) => {
        if (!programTypeList) return false;
        return !!programTypeList.find((programType) => programType.programTypeName === programTypeName);
    };

    // Get program types with definitions
    const programTypesWithDefinitions = programTypeList?.filter(
        (programType) => programType.programTypeDefinitions && programType.programTypeDefinitions.length > 0
    );

    // Get all available program types from the enum (excluding DEFAULT and KEMP)
    const allProgramTypes = Object.values(ProgramTypeNameEnum).filter(
        (typeName) => ![ProgramTypeNameEnum.DEFAULT, ProgramTypeNameEnum.KEMP].includes(typeName)
    );

    return (
        <Box type="outline" spacingInset="400">
            <Column>
                <Text type="h400">Program type</Text>
                <Toggle
                    label="Enable different rates per program type (DE Radio Plays, Household Sharing, etc.)"
                    value={hasProgramTypes}
                    isEditMode={false}
                />
                {hasProgramTypes && (
                    <Column>
                        <Text type="h300">Program types selected</Text>
                        {/* List all program types from the enum and show their status */}
                        {allProgramTypes.map((programTypeName) => (
                            <Toggle
                                key={programTypeName}
                                label={getCamelStrFromAllCaps(programTypeName, 'DE')}
                                value={hasProgramType(programTypeName)}
                                isEditMode={false}
                            />
                        ))}

                        {/* Render program type definitions if any exist */}
                        {programTypesWithDefinitions && programTypesWithDefinitions.length > 0 && (
                            <Column>
                                <Text type="h300">Program type definitions</Text>
                                <Table headerRows={1} showStripes={false} showDividers={true}>
                                    <TableRow>
                                        <TableCell>Program Type</TableCell>
                                        <TableCell>Definition</TableCell>
                                        <TableCell>Value</TableCell>
                                    </TableRow>
                                    {programTypesWithDefinitions.map((programType) =>
                                        programType.programTypeDefinitions?.map((definition, index) => (
                                            <TableRow key={`${programType.programTypeId}-${index}`}>
                                                <TableCell>
                                                    {getCamelStrFromAllCaps(programType.programTypeName ?? '', 'DE')}
                                                </TableCell>
                                                <TableCell>{definition.type}</TableCell>
                                                <TableCell>{definition.value}</TableCell>
                                            </TableRow>
                                        ))
                                    )}
                                </Table>
                            </Column>
                        )}
                    </Column>
                )}
            </Column>
        </Box>
    );
};
